import { useFieldInfo, useTsController } from "@ts-react/form";
import { type FunctionComponent, useId } from "react";
import { Fieldset, Label, RadioGroup, type RadioGroupProps, type SizeTokens, Theme, XStack, YStack } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { Paragraph2, ParagraphSmall } from "../Typography";
import { FormError } from "./FormError";
import { useFormFieldOptions } from "./FormFieldOptionsProvider";
type RadioGroupItem = {
  value: number;
  label: string;
  icon?: FunctionComponent<IconProps>;
  subLabel?: string;
};
function GroupItem({
  value,
  label,
  subLabel,
  icon: Icon,
  selected
}: RadioGroupItem & {
  selected: boolean;
}) {
  const id = `radiogroup-${value}`;
  return <XStack boc={selected ? "$borderColorFocus" : "$borderColor"} backgroundColor={selected ? "$backgroundFocus" : "$background"} bw={selected ? "$1" : "$0.5"} margin={selected ? "$0" : "$0.5"} br="$4" paddingHorizontal={selected ? 12 : "$3"} paddingVertical="$3" w="100%" data-sentry-element="XStack" data-sentry-component="GroupItem" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
      <RadioGroup.Item value={value.toString()} id={id} unstyled width="100%" height="100%" data-sentry-element="RadioGroup.Item" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
        <XStack alignItems="center" gap="$4" data-sentry-element="XStack" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
          {Icon ? <Icon size="$1.5" color={selected ? "$color10" : "$color8"} strokeWidth={selected ? 2 : 1} /> : null}
          <YStack gap="$2" pt="$1.5" pb="$1" data-sentry-element="YStack" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
            <Paragraph2 data-sentry-element="Paragraph2" data-sentry-source-file="AuthenticationRadioGroupField.tsx">{label}</Paragraph2>
            {subLabel ? <ParagraphSmall theme="alt1" lineHeight={12}>
                {subLabel}
              </ParagraphSmall> : null}
          </YStack>
        </XStack>
      </RadioGroup.Item>
    </XStack>;
}
export const AuthenticationRadioGroupField = ({
  options,
  native = false,
  ...props
}: {
  options: RadioGroupItem[];
  size?: SizeTokens;
} & Pick<RadioGroupProps, "native">) => {
  const {
    field,
    error,
    formState: {
      isSubmitting
    }
  } = useTsController<number>();
  const {
    label,
    isOptional
  } = useFieldInfo();
  const {
    hideOptionalLabel
  } = useFormFieldOptions();
  const id = useId();
  const disabled = isSubmitting;
  return <Theme name={error ? "red_alt3" : "granted"} forceClassName data-sentry-element="Theme" data-sentry-component="AuthenticationRadioGroupField" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
      <Fieldset data-sentry-element="Fieldset" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
        {label ? <Label theme="alt1" size={props.size || "$3"} htmlFor={`${id}_input`} pl="$0">
            {label}
            {isOptional && !hideOptionalLabel ? ` (Optional)` : null}
          </Label> : null}

        <RadioGroup disabled={disabled} aria-labelledby="Select one item" value={field.value ? field.value.toString() : ""} onValueChange={incoming => incoming ? field.onChange(parseInt(incoming, 10)) : null} id={`${id}_input`} key={`${id}_input`} native={native} name="form" data-sentry-element="RadioGroup" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
          <YStack alignItems="center" gap="$4" data-sentry-element="YStack" data-sentry-source-file="AuthenticationRadioGroupField.tsx">
            {options.map(item => {
            return <GroupItem key={item.value} value={item.value} label={item.label} subLabel={item.subLabel} selected={field.value === item.value} icon={item.icon} />;
          })}
          </YStack>
        </RadioGroup>
        <FormError errorMessage={error?.errorMessage} data-sentry-element="FormError" data-sentry-source-file="AuthenticationRadioGroupField.tsx" />
      </Fieldset>
    </Theme>;
};